import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AlertSeverity = 'info' | 'warn' | 'critical';
export type AlertSource = 'safety' | 'fleet' | 'telemetry' | 'insight' | 'plugin' | 'system' | 'mission';

export interface AppAlert {
  id: string;
  severity: AlertSeverity;
  source: AlertSource;
  title: string;
  body: string;
  ts: number;
  /** Robot ID this alert relates to, when applicable. */
  robot_id?: string;
  /** Read-state. */
  read: boolean;
  /** Optional inline action button label + intent (frontend stub today). */
  action?: { label: string; intent: 'rtl' | 'land' | 'open' | 'silence' };
}

interface NotificationsStore {
  alerts: Record<string, AppAlert>;
  /** Snoozed sources — silenced until window expires. */
  muted: Partial<Record<AlertSource, number>>;
  /** Persisted UI prefs. */
  audio: boolean;
  desktopPush: boolean;
  push: (a: Omit<AppAlert, 'id' | 'ts' | 'read'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clearAll: () => void;
  mute: (source: AlertSource, ms: number) => void;
  unmute: (source: AlertSource) => void;
  toggleAudio: () => void;
  toggleDesktopPush: () => void;
}

function makeId(): string {
  return `n-${Math.random().toString(36).slice(2, 10)}`;
}

export const useNotifications = create<NotificationsStore>()(
  persist(
    (set) => ({
      // Starts empty — real alerts arrive via `push` only. No demo seeds.
      alerts: {},
      muted: {},
      audio: false,
      desktopPush: true,
      push: (a) => {
        const id = makeId();
        const fresh: AppAlert = { ...a, id, ts: Date.now(), read: false };
        set((s) => ({ alerts: { ...s.alerts, [id]: fresh } }));
      },
      markRead: (id) =>
        set((s) => {
          const cur = s.alerts[id];
          if (!cur) return s;
          return { alerts: { ...s.alerts, [id]: { ...cur, read: true } } };
        }),
      markAllRead: () =>
        set((s) => ({
          alerts: Object.fromEntries(Object.entries(s.alerts).map(([k, v]) => [k, { ...v, read: true }])),
        })),
      remove: (id) =>
        set((s) => {
          const next = { ...s.alerts };
          delete next[id];
          return { alerts: next };
        }),
      clearAll: () => set({ alerts: {} }),
      mute: (source, ms) => set((s) => ({ muted: { ...s.muted, [source]: Date.now() + ms } })),
      unmute: (source) =>
        set((s) => {
          const next = { ...s.muted };
          delete next[source];
          return { muted: next };
        }),
      toggleAudio: () => set((s) => ({ audio: !s.audio })),
      toggleDesktopPush: () => set((s) => ({ desktopPush: !s.desktopPush })),
    }),
    // v2: v1 shipped seeded demo alerts; the key bump discards them.
    { name: 'fabricos.notifications.v2' },
  ),
);

export const SEVERITY_META: Record<AlertSeverity, { tone: 'good' | 'warn' | 'critical' | 'default'; label: string }> = {
  info: { tone: 'default', label: 'info' },
  warn: { tone: 'warn', label: 'warn' },
  critical: { tone: 'critical', label: 'critical' },
};

export const SOURCE_META: Record<AlertSource, { label: string; tone: 'accent' | 'warn' | 'critical' | 'good' | 'default' }> = {
  safety: { label: 'Safety', tone: 'critical' },
  fleet: { label: 'Fleet', tone: 'accent' },
  telemetry: { label: 'Telemetry', tone: 'warn' },
  insight: { label: 'Insight', tone: 'accent' },
  plugin: { label: 'Plugin', tone: 'accent' },
  system: { label: 'System', tone: 'default' },
  mission: { label: 'Mission', tone: 'good' },
};
